import { useCallback, useEffect, useMemo, useState } from 'react';
import { classesAPI, locationsAPI, subjectsAPI, timetableAPI, usersAPI } from '../services/api';
import { Clock3, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const scheduleDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const emptyForm = {
  class_ids: [],
  subject_ids: [],
  teacher_id: '',
  location_id: '',
  day_of_week: '',
  start_time: '',
  end_time: '',
};

const Timetable = () => {
  const { hasPermission, terminology, entityCategory } = useAuth();
  const scheduleLabel = terminology.schedules;
  const [entries, setEntries] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadData = useCallback(async () => {
    const [timetableRes, classesRes, locationsRes, subjectsRes, usersRes] = await Promise.all([
      timetableAPI.getAll(),
      classesAPI.getAll(),
      locationsAPI.getAll(),
      subjectsAPI.getAll(),
      usersAPI.getAll(),
    ]);

    setEntries(timetableRes.data.timetable || []);
    setSections(classesRes.data.classes || []);
    setLocations(locationsRes.data.locations || []);
    setSubjects(subjectsRes.data.subjects || []);
    setTeachers((usersRes.data.users || []).filter((user) => user.role === 'teacher'));
  }, []);

  const filteredSubjects = useMemo(() => {
    const selectedSections = sections.filter((item) => form.class_ids.includes(String(item.id)));
    if (!selectedSections.length) {
      return subjects.filter((subject) => subject.is_active);
    }
    return subjects.filter((subject) => subject.is_active && selectedSections.some((section) => {
      if (section.subject_assignments?.length) {
        return section.subject_assignments.some(
          (assignment) => Number(assignment.subject_id) === Number(subject.id)
        );
      }
      return !section.department_id || String(subject.department_id || '') === String(section.department_id);
    }));
  }, [subjects, sections, form.class_ids]);

  const availableSections = useMemo(() => {
    if (entityCategory !== 'office') return sections;
    const editingEntry = entries.find((entry) => Number(entry.id) === Number(editingId));
    const currentIds = new Set((editingEntry?.class_ids || []).map(String));
    const assignedIds = new Set(
      entries
        .filter((entry) => Number(entry.id) !== Number(editingId))
        .flatMap((entry) => (entry.class_ids || [entry.class_id]).filter(Boolean).map(String))
    );
    return sections.filter((section) => currentIds.has(String(section.id)) || !assignedIds.has(String(section.id)));
  }, [sections, entries, editingId, entityCategory]);

  const filteredTeachers = useMemo(() => {
    const section = sections.find((item) => String(item.id) === String(form.class_ids[0]));
    const subjectAssignment = section?.subject_assignments?.find(
      (assignment) => String(assignment.subject_id) === String(form.subject_ids[0])
    );

    if (subjectAssignment?.teacher_id) {
      return teachers.filter((teacher) => String(teacher.id) === String(subjectAssignment.teacher_id));
    }

    if (section?.department_id) {
      return teachers.filter((teacher) => String(teacher.department_id || '') === String(section.department_id));
    }

    return teachers;
  }, [teachers, sections, form.class_ids, form.subject_ids]);

  useEffect(() => {
    const allowedIds = new Set(filteredSubjects.map((subject) => String(subject.id)));
    setForm((current) => {
      const nextSubjectIds = current.subject_ids.filter((id) => allowedIds.has(id));
      return nextSubjectIds.length === current.subject_ids.length
        ? current
        : { ...current, subject_ids: nextSubjectIds };
    });
  }, [filteredSubjects]);

  useEffect(() => {
    loadData()
      .catch(() => toast.error(`Failed to load ${scheduleLabel.toLowerCase()}`))
      .finally(() => setLoading(false));
  }, [loadData, scheduleLabel]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.class_ids.length || !form.subject_ids.length || !form.day_of_week || !form.start_time || !form.end_time) {
      toast.error(`${terminology.group}, ${terminology.area.toLowerCase()}, day, start time, and end time are required`);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        class_id: form.class_ids[0],
        subject_id: form.subject_ids[0],
        teacher_id: form.teacher_id || null,
        location_id: form.location_id || null,
      };

      if (editingId) {
        await timetableAPI.update(editingId, payload);
        toast.success(`${terminology.schedule} updated`);
      } else {
        await timetableAPI.create(payload);
        toast.success(`${terminology.schedule} created`);
      }

      resetForm();
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save shift');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      class_ids: (entry.class_ids || [entry.class_id]).filter(Boolean).map(String),
      subject_ids: (entry.subject_ids || [entry.subject_id]).filter(Boolean).map(String),
      teacher_id: String(entry.teacher_id || ''),
      location_id: String(entry.location_id || ''),
      day_of_week: entry.day_of_week || '',
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this shift?')) {
      return;
    }

    try {
      await timetableAPI.remove(id);
      toast.success(`${terminology.schedule} removed`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove shift');
    }
  };

  const canManage = hasPermission('timetable.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{terminology.schedules}</h1>
          <p className="text-gray-500 text-sm mt-1">Map {terminology.groups.toLowerCase()} to locations, {terminology.areas.toLowerCase()}, {terminology.leaders.toLowerCase()}, and attendance hours.</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowForm((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : `Add ${terminology.schedule}`}
          </button>
        )}
      </div>

      {showForm && canManage && (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.groups}</label>
            <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-gray-300 bg-white p-3">
              {availableSections.map((section) => {
                const value = String(section.id);
                const checked = form.class_ids.includes(value);
                return (
                  <label key={section.id} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setForm((current) => {
                        const classIds = checked
                          ? current.class_ids.filter((id) => id !== value)
                          : [...current.class_ids, value];
                        return { ...current, class_ids: classIds };
                      })}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    {section.name}
                  </label>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500">{form.class_ids.length} selected</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Office Location</label>
            <select
              value={form.location_id}
              onChange={(e) => setForm({ ...form, location_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.areas}</label>
            <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-gray-300 bg-white p-3">
              {filteredSubjects.map((subject) => {
                const value = String(subject.id);
                const checked = form.subject_ids.includes(value);
                return (
                  <label key={subject.id} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setForm((current) => ({
                        ...current,
                        subject_ids: checked
                          ? current.subject_ids.filter((id) => id !== value)
                          : [...current.subject_ids, value],
                      }))}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    {subject.name}{subject.code ? ` (${subject.code})` : ''}
                  </label>
                );
              })}
              {!filteredSubjects.length && <p className="text-xs text-amber-700">No work area is mapped to the selected teams.</p>}
            </div>
            <p className="mt-1 text-xs text-gray-500">{form.subject_ids.length} selected</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.leader}</label>
            <select
              value={form.teacher_id}
              onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select supervisor</option>
              {filteredTeachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>{teacher.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Day</label>
            <select
              value={form.day_of_week}
              onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select day</option>
              {scheduleDays.map((day) => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Start Time</label>
            <input
              type="time"
              value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">End Time</label>
            <input
              type="time"
              value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2 xl:col-span-3 flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : editingId ? `Update ${terminology.schedule}` : `Create ${terminology.schedule}`}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading shifts...</div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No shifts created yet</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {entries.map((entry) => (
              <div key={entry.id} className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
                    <Clock3 size={18} />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">{entry.section_name}</h2>
                    <p className="text-sm text-gray-500">
                      {entry.department_name || 'No department'} • {entry.subject_name || 'No work area'} • {entry.location_name || 'No location'}
                    </p>
                    <p className="mt-1 text-sm text-gray-700">
                      {entry.day_of_week} • {entry.start_time} to {entry.end_time}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {entry.teacher_name || 'No supervisor assigned'} {entry.subject_code ? `• ${entry.subject_code}` : ''}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  {canManage ? (
                    <>
                      <button
                        onClick={() => startEdit(entry)}
                        className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
                      >
                        <Pencil size={12} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={12} />
                        Remove
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">View only</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Timetable;
